import { supabase } from './supabase'
import type { Notification, NotificationAction } from './types'

export type NotifiableType = 'comment' | 'reaction' | 'follow' | 'mention' | 'episode_summary'

export async function getNotifications(
  userId: string,
  limit = 20,
  offset = 0,
): Promise<Notification[]> {
  const { data } = await supabase
    .from('notifications')
    .select(
      `
      *,
      actor:actor_id (
        username,
        display_name,
        avatar_url
      )
    `,
    )
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1)

  return ((data ?? []) as unknown[]).map((n) => {
    const row = n as Record<string, unknown>
    return {
      ...row,
      actor_username: (row.actor as Record<string, unknown> | null)
        ?.username as string | undefined,
      actor_display_name: (row.actor as Record<string, unknown> | null)
        ?.display_name as string | undefined,
      actor_avatar: (row.actor as Record<string, unknown> | null)
        ?.avatar_url as string | undefined,
    } as Notification
  })
}

export async function getUnreadCount(userId: string): Promise<number> {
  const { count } = await supabase
    .from('notifications')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('read', false)

  return count ?? 0
}

export async function markAsRead(notificationId: string): Promise<void> {
  await supabase
    .from('notifications')
    .update({ read: true })
    .eq('id', notificationId)
}

export async function markAllAsRead(userId: string): Promise<void> {
  await supabase
    .from('notifications')
    .update({ read: true })
    .eq('user_id', userId)
    .eq('read', false)
}

// Forem-style notification creation with dedup support
export async function createNotification(
  userId: string,
  actorId: string | null,
  action: NotificationAction,
  notifiableType: NotifiableType,
  notifiableId?: string | null,
  body?: string | null,
): Promise<void> {
  if (action === 'reaction' && body) {
    const existing = await supabase
      .from('notifications')
      .select('id')
      .eq('user_id', userId)
      .eq('notifiable_type', notifiableType)
      .eq('notifiable_id', notifiableId ?? '')
      .eq('action', action)
      .limit(1)

    if ((existing.data ?? []).length > 0) {
      // Already notified about this same action on same object
      await supabase
        .from('notifications')
        .update({ read: false, created_at: new Date().toISOString() })
        .eq('id', existing.data![0].id)
      return
    }
  }

  // Check unique constraint by attempting upsert
  const { error } = await supabase.from('notifications').upsert(
    {
      user_id: userId,
      actor_id: actorId,
      action,
      notifiable_type: notifiableType,
      notifiable_id: notifiableId ?? null,
      body,
      read: false,
    },
    {
      onConflict: 'user_id,notifiable_type,notifiable_id,action',
      ignoreDuplicates: true,
    },
  )

  if (error) {
    console.error('Failed to create notification:', error)
  }
}

// Forem-style notification message builder
const NOTIFICATION_MESSAGES: Record<
  NotificationAction,
  { verb: string; icon: string; defaultBody: string }
> = {
  comment: { verb: 'commented on', icon: '💬', defaultBody: 'your post' },
  reaction: { verb: 'reacted to', icon: '⭐', defaultBody: 'your post' },
  follow: { verb: 'followed you', icon: '👤', defaultBody: '' },
  mention: { verb: 'mentioned you in', icon: '@', defaultBody: 'a post' },
  published: { verb: 'published', icon: '📝', defaultBody: 'a new post' },
  milestone: { verb: 'hit a milestone with', icon: '🏆', defaultBody: 'your content' },
}

export function getNotificationMessage(notification: Notification): string {
  const config =
    NOTIFICATION_MESSAGES[notification.action] ?? {
      verb: notification.action,
      icon: '📌',
      defaultBody: '',
    }
  const actor =
    notification.actor_display_name ??
    notification.actor_username ??
    'Someone'
  const target = notification.body ?? config.defaultBody
  return `${config.icon} ${actor} ${config.verb} ${target}`
}

// Real-time subscription with unique channel names (avoids React StrictMode conflicts)
export function subscribeToNotifications(
  userId: string,
  callback: (notification: Notification) => void,
) {
  const channelName = `notifications:${userId}:${Date.now()}`
  const channel = supabase
    .channel(channelName)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${userId}`,
      },
      (payload) => {
        callback(payload.new as Notification)
      },
    )
    .subscribe()

  return () => {
    supabase.removeChannel(channel)
  }
}

// Send notifications to all followers of a user (Forem-style)
export async function notifyFollowers(
  userId: string,
  actorId: string,
  action: NotificationAction,
  notifiableType: NotifiableType,
  notifiableId: string,
  body?: string,
): Promise<void> {
  const { data: followers } = await supabase
    .from('follows')
    .select('follower_id')
    .eq('followable_id', userId)
    .eq('followable_type', 'user')
    .eq('subscription_status', 'all_articles')

  if (!followers || followers.length === 0) return

  const notifications = followers.map((f) => ({
    user_id: f.follower_id,
    actor_id: actorId,
    action,
    notifiable_type: notifiableType,
    notifiable_id: notifiableId,
    body: body ?? null,
    read: false,
  }))

  // Batch insert with dedup - skip errors from duplicates
  const { error } = await supabase.from('notifications').upsert(notifications, {
    onConflict: 'user_id,notifiable_type,notifiable_id,action',
    ignoreDuplicates: true,
  })

  if (error) {
    console.error('Failed to send follower notifications:', error)
  }
}
